import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchAdPlan } from "../api/adsApi";

const INITIAL_BATCH_SIZE = 12;
const PREFETCH_THRESHOLD = 3;
const AD_PLAN_REFRESH_INTERVAL_MS = 15 * 60 * 1000;
const AD_PLAN_REFRESH_BUSY_RETRY_MS = 30 * 1000;
const SESSION_STORAGE_PREFIX = "chekhabare:ad-session:";

const createRandomSessionId = () => {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    return `ckh_${Array.from(bytes, (value) =>
      value.toString(16).padStart(2, "0")
    ).join("")}`;
  }

  return `ckh_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 14)}`;
};

const getSessionStorageKey = ({ surface, device, feedKey }) => {
  const scope = encodeURIComponent(
    `${surface}|${device}|${String(feedKey || "default")}`
  );

  return `${SESSION_STORAGE_PREFIX}${scope}`;
};

const getOrCreateSessionId = ({ surface, device, feedKey }) => {
  const fallback = createRandomSessionId();

  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    const storageKey = getSessionStorageKey({
      surface,
      device,
      feedKey,
    });
    const existing = window.sessionStorage.getItem(storageKey);

    if (/^[A-Za-z0-9_-]{8,128}$/.test(existing || "")) {
      return existing;
    }

    window.sessionStorage.setItem(storageKey, fallback);
  } catch (_error) {
    // Storage is an optimization only. Ad serving must still work when the
    // browser blocks sessionStorage or runs in a restricted webview.
  }

  return fallback;
};

const createEmptyState = () => ({
  ready: false,
  enabled: false,
  first: null,
  regular: [],
  houseAd: null,
  regularEvery: 0,
  nextCursor: 0,
  version: "",
  expiresAt: "",
});

const useAdPlan = ({
  enabled = true,
  surface,
  device,
  feedKey = "default",
  newsCount = 0,
}) => {
  const sessionId = useMemo(
    () =>
      getOrCreateSessionId({
        surface,
        device,
        feedKey,
      }),
    [device, feedKey, surface]
  );

  const [state, setState] = useState(createEmptyState);
  const abortRef = useRef(null);
  const requestInFlightRef = useRef(false);
  const generationRef = useRef(0);
  const lastFullRefreshAtRef = useRef(0);

  const cancelRequest = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    requestInFlightRef.current = false;
  }, []);

  const loadBatch = useCallback(
    async ({ cursor = 0, replace = false } = {}) => {
      if (!enabled || requestInFlightRef.current) {
        return false;
      }

      requestInFlightRef.current = true;
      const requestGeneration = generationRef.current;
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const plan = await fetchAdPlan({
          surface,
          device,
          sessionId,
          batch: INITIAL_BATCH_SIZE,
          cursor,
          signal: controller.signal,
        });

        if (
          controller.signal.aborted ||
          requestGeneration !== generationRef.current
        ) {
          return;
        }

        setState((previous) => ({
          ready: true,
          enabled: plan.enabled,
          first: replace ? plan.first : previous.first ?? plan.first,
          regular:
            replace || cursor === 0
              ? plan.regular
              : [...previous.regular, ...plan.regular],
          regularEvery: plan.regularEvery,
          // House Ad has an independent runtime version and is not part of
          // commercial scheduling, so a later batch may safely refresh its
          // copy/toggles without reshuffling real ads.
          houseAd: plan.houseAd,
          nextCursor: plan.nextCursor,
          version: plan.version,
          expiresAt: plan.expiresAt,
        }));

        if (replace && cursor === 0) {
          lastFullRefreshAtRef.current = Date.now();
        }

        return true;
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        // Advertising is non-critical. A failed ad request must never block,
        // replace, or surface an error inside the news feed.
        if (process.env.NODE_ENV !== "production") {
          console.warn("[ads] plan request failed", error);
        }

        if (requestGeneration === generationRef.current) {
          setState((previous) => ({
            ...previous,
            ready: true,
            enabled: false,
            first: null,
            regular: [],
            houseAd: null,
            nextCursor: 0,
          }));
        }

        return false;
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          requestInFlightRef.current = false;
        }
      }
    },
    [device, enabled, sessionId, surface]
  );

  useEffect(() => {
    generationRef.current += 1;
    cancelRequest();
    setState(createEmptyState());
    lastFullRefreshAtRef.current = Date.now();

    if (!enabled) {
      setState((previous) => ({
        ...previous,
        ready: true,
      }));
      return undefined;
    }

    loadBatch({ cursor: 0, replace: true });

    return () => {
      generationRef.current += 1;
      cancelRequest();
    };
  }, [
    cancelRequest,
    device,
    enabled,
    feedKey,
    loadBatch,
    sessionId,
    surface,
  ]);

  useEffect(() => {
    if (!enabled || typeof document === "undefined") {
      return undefined;
    }

    let timerId = null;
    let disposed = false;

    const clearTimer = () => {
      if (timerId !== null) {
        clearTimeout(timerId);
        timerId = null;
      }
    };

    const scheduleNextRefresh = (delayMs) => {
      clearTimer();

      if (disposed || document.visibilityState !== "visible") {
        return;
      }

      timerId = setTimeout(async () => {
        timerId = null;

        if (disposed || document.visibilityState !== "visible") {
          return;
        }

        if (requestInFlightRef.current) {
          scheduleNextRefresh(AD_PLAN_REFRESH_BUSY_RETRY_MS);
          return;
        }

        await loadBatch({
          cursor: 0,
          replace: true,
        });

        if (disposed) {
          return;
        }

        scheduleNextRefresh(AD_PLAN_REFRESH_INTERVAL_MS);
      }, Math.max(0, delayMs));
    };

    const scheduleFromLastRefresh = () => {
      const elapsed = Math.max(
        0,
        Date.now() - lastFullRefreshAtRef.current
      );
      const remaining = Math.max(
        0,
        AD_PLAN_REFRESH_INTERVAL_MS - elapsed
      );

      scheduleNextRefresh(remaining);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        clearTimer();
        return;
      }

      scheduleFromLastRefresh();
    };

    scheduleFromLastRefresh();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      disposed = true;
      clearTimer();
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [enabled, loadBatch]);

  const requiredRegularSlots = useMemo(() => {
    if (!state.enabled || state.regularEvery <= 0) {
      return 0;
    }

    return Math.floor(
      Math.max(0, Number(newsCount) || 0) / state.regularEvery
    );
  }, [newsCount, state.enabled, state.regularEvery]);

  useEffect(() => {
    if (!state.ready || !state.enabled || state.regularEvery <= 0) {
      return;
    }

    const remainingPreparedSlots =
      state.regular.length - requiredRegularSlots;

    if (remainingPreparedSlots > PREFETCH_THRESHOLD) {
      return;
    }

    loadBatch({
      cursor: state.nextCursor,
      replace: false,
    });
  }, [
    loadBatch,
    requiredRegularSlots,
    state.enabled,
    state.nextCursor,
    state.ready,
    state.regular.length,
    state.regularEvery,
  ]);

  return {
    ready: state.ready,
    enabled: state.enabled,
    first: state.first,
    regular: state.regular,
    regularEvery: state.enabled ? state.regularEvery : 0,
    houseAd: state.enabled ? state.houseAd : null,
    version: state.version,
    expiresAt: state.expiresAt,
  };
};

export default useAdPlan;
